import { getNodesBounds, getViewportForBounds, type ReactFlowInstance } from '@xyflow/react';
import { toPng, toSvg } from 'html-to-image';
import { useDiagramViewStore } from '../store/useDiagramViewStore';

function waitForNextPaint(): Promise<void> {
  return new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));
}

/**
 * The canvas culls off-screen nodes/edges from the DOM entirely (onlyRenderVisibleElements)
 * for drag performance on large diagrams. That means a naive export only ever contains
 * whatever happens to already be scrolled into view — no transform math applied to the
 * exported clone can conjure up DOM nodes that were never mounted. This forces every node
 * to mount (and get measured) first, runs `fn`, then restores culling either way.
 */
async function withEveryNodeMounted<T>(fn: () => Promise<T>): Promise<T> {
  const { setForceRenderAll } = useDiagramViewStore.getState();
  setForceRenderAll(true);
  try {
    await waitForNextPaint();
    // ResizeObserver measurement callbacks land on the next microtask/frame after
    // mount, not synchronously within it — a short settle delay avoids racing them.
    await new Promise((resolve) => setTimeout(resolve, 80));
    return await fn();
  } finally {
    setForceRenderAll(false);
  }
}

export function downloadTextFile(filename: string, contents: string, mime = 'text/plain') {
  const blob = new Blob([contents], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function downloadDataUrl(dataUrl: string, filename: string) {
  const a = document.createElement('a');
  a.href = dataUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

const EXPORT_PADDING = 48;

/**
 * Computes the pixel dimensions + viewport transform needed to render every node — not
 * just whatever is currently visible on screen — at a natural (non-clipped) size.
 */
function computeFullDiagramFrame(reactFlow: ReactFlowInstance) {
  const bounds = getNodesBounds(reactFlow.getNodes());
  const width = Math.max(Math.ceil(bounds.width + EXPORT_PADDING * 2), 1);
  const height = Math.max(Math.ceil(bounds.height + EXPORT_PADDING * 2), 1);
  // getViewportForBounds's padding is a fraction of width/height by default (or a
  // "Npx" string) — NOT a raw pixel count. Passing the pixel constant as a bare number
  // would be read as e.g. "4800% padding" and zoom the whole diagram down to a speck.
  const viewport = getViewportForBounds(bounds, width, height, 0.1, 2, `${EXPORT_PADDING}px`);
  return { width, height, viewport };
}

/**
 * html-to-image is pointed at `.react-flow__viewport` (the layer that holds only nodes
 * and edges) rather than the whole canvas container, so Controls/the interaction-mode
 * toggle/toasts never end up in the image. Its `style` override is applied only to the
 * cloned DOM html-to-image rasterizes — the real, on-screen viewport/pan/zoom is
 * untouched — which is also what lets this render every node regardless of what's
 * currently scrolled into view.
 */
function findViewportElement(containerEl: HTMLElement): HTMLElement {
  const el = containerEl.querySelector<HTMLElement>('.react-flow__viewport');
  if (!el) throw new Error('Could not find the diagram viewport to export.');
  return el;
}

export async function exportDiagramAsPng(
  containerEl: HTMLElement,
  reactFlow: ReactFlowInstance,
  filename: string,
  backgroundColor = '#eef0f4',
) {
  await withEveryNodeMounted(async () => {
    const viewportEl = findViewportElement(containerEl);
    const { width, height, viewport } = computeFullDiagramFrame(reactFlow);
    const dataUrl = await toPng(viewportEl, {
      backgroundColor,
      width,
      height,
      pixelRatio: 2,
      style: {
        width: `${width}px`,
        height: `${height}px`,
        transform: `translate(${viewport.x}px, ${viewport.y}px) scale(${viewport.zoom})`,
      },
    });
    downloadDataUrl(dataUrl, filename);
  });
}

export async function exportDiagramAsSvg(
  containerEl: HTMLElement,
  reactFlow: ReactFlowInstance,
  filename: string,
  backgroundColor = '#eef0f4',
) {
  await withEveryNodeMounted(async () => {
    const viewportEl = findViewportElement(containerEl);
    const { width, height, viewport } = computeFullDiagramFrame(reactFlow);
    const dataUrl = await toSvg(viewportEl, {
      backgroundColor,
      width,
      height,
      style: {
        width: `${width}px`,
        height: `${height}px`,
        transform: `translate(${viewport.x}px, ${viewport.y}px) scale(${viewport.zoom})`,
      },
    });
    downloadDataUrl(dataUrl, filename);
  });
}
